import { useMemo } from "react";
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { formatINR } from "@/utils/app-formatters";
import {
  DEFAULT_SABIL_OUTSTANDING_MIN_PERCENT,
  SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
} from "./utils";
import { usePreviewSabilOutstandingRecipients } from "./hooks";
import {
  formatSabilOutstandingRateColumnLabel,
  formatSabilOutstandingTypeLabel,
} from "./sabil-outstanding-type-label";
import OutstandingRecipientPanel, {
  type OutstandingRecipientColumn,
  type OutstandingRecipientPanelProps,
} from "./OutstandingRecipientPanel";

type SabilOutstandingRecipientPanelProps = Pick<
  OutstandingRecipientPanelProps,
  "onPreviewCount" | "onPreviewed" | "onSelectionChange"
>;

export default function SabilOutstandingRecipientPanel(props: SabilOutstandingRecipientPanelProps) {
  const columns = useMemo(
    () =>
      (sabilType: string): OutstandingRecipientColumn[] => [
        {
          key: "member",
          header: "Member",
          render: (r) => String(r.recipientName ?? "—"),
          compactGroup: "identity",
        },
        {
          key: "memberIts",
          header: "Member ITS",
          render: (r) => String(r.memberItsNo ?? "—"),
          compactGroup: "identity",
        },
        {
          key: "role",
          header: "Role",
          render: (r) => String(r.memberRole ?? "—"),
          compactGroup: "identity",
        },
        {
          key: "phone",
          header: "Phone",
          render: (r) => String(r.phone ?? "—"),
          compactGroup: "identity",
        },
        {
          key: "sabil",
          header: "Sabil",
          render: (r) => String(r.name ?? "—"),
          compactGroup: "identity",
        },
        {
          key: "type",
          header: "Type",
          render: (r) => formatSabilOutstandingTypeLabel(r.sabilType),
          compactGroup: "identity",
        },
        {
          key: "hofIts",
          header: "HOF ITS",
          render: (r) => String(r.itsNo ?? "—"),
          compactGroup: "identity",
        },
        {
          key: "rate",
          header: formatSabilOutstandingRateColumnLabel(sabilType),
          align: "right",
          render: (r) => formatINR(r.monthlyTakhmeenAmount, { empty: "—" }),
        },
        {
          key: "due",
          header: "Due",
          align: "right",
          render: (r) => formatINR(r.takhmeenAmount, { empty: "—" }),
        },
        {
          key: "paid",
          header: "Paid",
          align: "right",
          render: (r) => formatINR(r.paidBalance, { empty: "—" }),
        },
        {
          key: "pending",
          header: "Pending",
          align: "right",
          render: (r) => formatINR(r.pendingBalance, { empty: "—" }),
        },
        {
          key: "outstandingPct",
          header: "Outstanding %",
          align: "right",
          render: (r) =>
            r.outstandingPercent != null ? `${Number(r.outstandingPercent).toFixed(1)}%` : "—",
        },
      ],
    []
  );

  return (
    <OutstandingRecipientPanel
      {...props}
      sabilTypeDefault="CHULA"
      usePreviewRecipients={usePreviewSabilOutstandingRecipients}
      applyFieldDefaults={({ minOutstandingPercent, notifyMode, setValue }) => {
        if (minOutstandingPercent == null) {
          setValue("minOutstandingPercent", DEFAULT_SABIL_OUTSTANDING_MIN_PERCENT, {
            shouldDirty: false,
          });
        }
        if (!notifyMode) {
          setValue("notifyMode", SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY, {
            shouldDirty: false,
          });
        }
      }}
      introText={(sabilType) => (
        <>
          Reminds open {formatSabilOutstandingTypeLabel(sabilType)} sabils with outstanding
          balances, using the {formatSabilOutstandingTypeLabel(sabilType)} template only. Switch
          tabs to send the other Sabil type separately — the two are never mixed in one broadcast.
        </>
      )}
      itsAutocompleteLabel="Household ITS"
      minPercentHelperText="Include sabils with pending balance and outstanding % at or above this value (default 1)."
      renderNotifyModeSection={({ notifyMode, setValue, invalidatePreview }) => (
        <FormControl component="fieldset" sx={{ mt: 1 }}>
          <FormLabel component="legend">Notify</FormLabel>
          <RadioGroup
            row
            value={notifyMode ?? SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY}
            onChange={(e) => {
              setValue("notifyMode", e.target.value, { shouldDirty: true });
              invalidatePreview();
            }}
          >
            <FormControlLabel
              value={SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY}
              control={<Radio />}
              label="Household + family (recommended)"
            />
            <FormControlLabel
              value={SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY}
              control={<Radio />}
              label="Household contact only"
            />
          </RadioGroup>
        </FormControl>
      )}
      describeSendMode={(notifyMode) =>
        `one per unique phone per sabil, ${
          notifyMode === SABIL_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
            ? "household contact only"
            : "household + family"
        }`
      }
      columns={columns}
      getRowKey={(row, index) =>
        `${String(row.sabilId ?? "")}-${String(row.memberItsNo ?? row.phone ?? "")}-${index}`
      }
      extractRowItsId={(row) => row.memberItsNo ?? row.itsNo}
    />
  );
}
